/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DataPointSelections
 * @hideconstructor
 */
define([
	'underscore',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/DataPointSelectionsAPI',
	'./api/deprecated/DataPointSelectionsAPI',
	'./api/DataPointSelectionsAPISpec',
	'../../../../util/ContentUtil',
	'../../../../lib/@waca/dashboard-common/dist/utils/ContentUtil',
	'./VisSelected',
	'./PendingSelections'
], (_, APIFactory, DataPointSelectionsAPI, DeprecatedDataPointSelectionsAPI, DataPointSelectionsAPISpec, ContentUtil, CommonContentUtil, VisSelected, PendingSelections) => {

	class DataPointSelections extends DataPointSelectionsAPISpec {

		constructor(options){
			super(options);

			this.content = options.content;
			this.dashboard = options.dashboardAPI;
		}

		getAPI(type) {
			if (type === 'deprecated') {
				if (!this._deprecatedApi) {
					this._deprecatedApi = APIFactory.createAPI(this, [DataPointSelectionsAPI, DeprecatedDataPointSelectionsAPI]);
				}
				return this._deprecatedApi;
			}
			if (type === 'pending') {
				if (!this._pending) {
					const pendingSelections = new PendingSelections();
					this._pending = APIFactory.createAPI(pendingSelections , [DataPointSelectionsAPI]);
				}
				return this._pending;
			}

			if (!this._api) {
				this._api = APIFactory.createAPI(this, [DataPointSelectionsAPI]);
			}
			return this._api;
		}

		select(selections, transactionToken) {
			const legacySelection = this._getLegacySelection(selections);
			const multiTuple = selections && selections.length > 1;
			const controller = this._getController();
			return controller.select({
				itemIds: legacySelection.columnIds,
				tuple: legacySelection.tuple,
				command: 'update',
				slotsToClear: [],
				pending: false,
				edgeSelect: legacySelection.edgeSelect,
				multiTuple: multiTuple
			}, transactionToken);
		}

		/**
		 * deselect current selections.
		 * @param {*} selections The selected items to deselect
		 * @param {*} transactionToken the transaction Token
		 * @param {*} forceClearSlots if true, clear all slots regardless if there is a selection
		 */
		deselect(selections, transactionToken, forceClearSlots=false) {
			const legacySelection = this._getLegacySelection(selections);
			const multiTuple = selections && selections.length > 1;
			const visualization = this.content.getFeature('Visualization');
			const controller = this._getController();
			const _slotsToClear = forceClearSlots ? visualization.getSlots().getMappedSlotList() : this._getSlotsToClear(selections);
			return controller.select({
				itemIds: legacySelection.columnIds,
				tuple: legacySelection.tuple,
				command: 'remove',
				slotsToClear: _slotsToClear,
				pending: false,
				edgeSelect: legacySelection.edgeSelect,
				multiTuple: multiTuple
			}, transactionToken);
		}

		_getSelectionValues(selections) {
			// Default. Use the current selections
			return _.chain(selections || this.getSelections())
				.map(selection => selection.categories)
				.flatten()
				.map(selection => selection.value)
				.value();
		}

		_getSlotsToClear(selections) {
			const values = this._getSelectionValues(selections);
			const current = this._getSelectionValues();
			if (values.length === current.length && !_.difference(values, current).length) {
				const visualization = this.content.getFeature('Visualization');
				return visualization.getSlots().getMappedSlotList();
			}
			return [];
		}

		clearAll(transactionToken) {
			const selections = this._buildSelections(/*selectedDataItemList*/[], /*strictMatch*/ false) || [];
			return this.deselect(selections, transactionToken, true);
		}

		_getVisualization() {
			if (!this.visualization) {
				this.visualization = this.content.getFeature('Visualization');
			}
			return this.visualization;
		}

		_getController() {
			if (!this.controller) {
				this.controller = this.content.getFeature('SelectionController.deprecated');
			}
			return this.controller;
		}

		_getLegacySelection (selections) {
			const legacySelection = {
				columnIds: [],
				tuple: [],
				edgeSelect: !!_.find(selections, (selection) => selection.edgeSelect)
			};
			selections.forEach(selection => {
				const values = [];
				const addedValues = [];
				selection.categories.forEach(category => {

					if (!legacySelection.columnIds.includes(category.columnId)) {
						legacySelection.columnIds.push(category.columnId);
					}

					if(!addedValues.includes(category.columnId+category.value)){
						values.push({ u: category.value, d: category.label });
						addedValues.push(category.columnId+category.value);
					}
				});
				if (values.length) {
					legacySelection.tuple.push(values);
				}
			});
			return legacySelection;
		}

		getSelections(selectedDataItemList = []) {
			return this._buildSelections(selectedDataItemList);
		}

		_getScope() {
			const type = this.dashboard.getAppConfig('pageContainerType');
			const page = CommonContentUtil.getPageContent(this.content, type);
			const scope = page && page.getId();
			return scope;
		}

		_getEventGroupId() {
			const eventGroups = this.dashboard.getFeature('EventGroups');
			const eventGroupId = eventGroups.getGroupId(this.content.getId());
			return eventGroupId;
		}

		_getPageContext() {
			if (!this._pageContext) {
				this._pageContext = ContentUtil.getPageContext(this.dashboard);
			}
			return this._pageContext;
		}

		_buildSelections(selectedDataItemList, strictMatch = true) {
			let dataPointSelections = [];
			const dataSource = this._getVisualization().getDataSource();
			const sourceId = dataSource && dataSource.getId();
			if (sourceId) {
				const selectors = {
					scope: this._getScope(),
					eventGroupId : this._getEventGroupId(),
					sourceId: sourceId,
					origin: 'visualization',
					_strictMatch: strictMatch
				};
				const slots = this._getVisualization().getSlots();
				const mappedDataItemList = slots.getDataItemList();

				/**
				 * If there is either brushing or pending filters collect data points or edge values.
				 * Note: pageContextEntry.getDataPoints() returns an array of array, i.e. an array of tuples,
				 * while pageContextEntry.getValues() returns an array of values.
				 */
				const netPageContext = this._getPageContext().getNetPageContextItems(selectors);
				dataPointSelections.push(...this._getValues(netPageContext, selectedDataItemList, mappedDataItemList));

				if (dataPointSelections.length) {
					dataPointSelections = this._uniqueValues(dataPointSelections);
				}
			}

			return dataPointSelections;
		}

		_getValues(pageContextEntries, selectedDataItemList, mappedDataItemList) {
			let payloadValueList = [];
			let payloadValue = null;
			let dataItem = null;
			if (pageContextEntries && pageContextEntries.length) {
				pageContextEntries.forEach(pEntry => {
					const columnIdList =  pEntry.getItemIds();
					if (pEntry.isDataPointType()) {
						let payloadTupleList = [];
						const tupleList = pEntry.getDataPoints();
						tupleList.forEach(tuple => {
							let payloadTuple = [];
							tuple.forEach((value, valueIdx) => {
								dataItem = this._getDataItem(columnIdList[valueIdx], selectedDataItemList, mappedDataItemList);
								if (dataItem && dataItem.getType() === 'attribute') {
									payloadValue = this._toPayLoadValue(value, columnIdList[valueIdx], dataItem.getId());
									payloadTuple.push(payloadValue);
								}
							});
							payloadTupleList.push({
								categories: payloadTuple
							});
						});
						payloadValueList.push(...payloadTupleList);
					} else {
						const values = pEntry.getValues();
						values.forEach(value => {
							dataItem = this._getDataItem(columnIdList[0], selectedDataItemList, mappedDataItemList);
							if (dataItem && dataItem.getType() === 'attribute') {
								payloadValue = this._toPayLoadValue(value, columnIdList[0], dataItem.getId());
								payloadValueList.push({
									categories: [payloadValue]
								});
							}
						});
					}
				});
			}
			return payloadValueList;
		}

		_getDataItem(columnId, selectedDataItemList, mappedDataItemList) {
			let dataItem = _.find(selectedDataItemList, dataItem => dataItem.getColumnId() === columnId);
			if (!dataItem) {
				dataItem = _.find(mappedDataItemList, dataItem => dataItem.getColumnId() === columnId);
			}
			return dataItem;
		}

		_toPayLoadValue(value, columnId, dataItemId) {
			let resultPayLoad = {
				dataItemId: dataItemId,
				columnId: columnId,
				value: value.u,
				label: value.d
			};
			if (value.p) {
				resultPayLoad.parentId = value.p.u;
			}
			return  resultPayLoad;
		}

		_uniqueValues(dataPointSelections = []) {
			let uniqueValues = [];
			let valuesMap = {};
			dataPointSelections.forEach(dataPoint => {
				const categoryKey = _.pluck(dataPoint.categories, 'value').join();
				if (!valuesMap[categoryKey]) {
					valuesMap[categoryKey] = 1;
					uniqueValues.push(dataPoint);
				}
			});
			return uniqueValues;
		}

		isSelected(selectionPayload) {
			const resolvedSelections = this._getLegacySelection(selectionPayload);
			return resolvedSelections.edgeSelect ? this._isEdgeSelected(selectionPayload) : this._isDataPointSelected(resolvedSelections);
		}

		/**
		 * @return {boolean} true if the resolved items are identical to the current selection
		 */
		_isDataPointSelected(resolvedSelections) {
			const selection = this.getSelection(resolvedSelections.columnIds);
			return selection ? selection.isSelected(_.flatten(resolvedSelections.tuple)) : false;
		}

		/**
		 * @return {boolean} true if the resolved items are identical to the current selection
		 */
		_isEdgeSelected(selections) {
			const getSelectedEdgeValues = (columnId) => {
				return this._getController().getSelectedTuples([columnId]);
			};

			// The format of resolved and the result of visSelectionController.getSelectedTuples() do not match.
			// Perform a sequence of operations to transform the output of getSelectedTuples() into the same format as resolved.
			const selectionTuples = _.chain(_.map(this._getVisualization().getSlots().getDataItemList(), dataItem => {
				const columnId = dataItem.getColumnId();
				const values = getSelectedEdgeValues(columnId);
				return _.map(values, value => {
					value.label = _.isUndefined(value.label) ? value.d : value.label;
					value.value = _.isUndefined(value.value) ? value.u : value.value;
					return {
						columnId,
						tupleItem: value
					};
				});

			}))
				.flatten()
				.filter(itemTuplePair => !!itemTuplePair.tupleItem) // Remove items with empty tuples
				.value(); // End sequence of transformations - get final array

			// check if the resolved tuple is a subset of the selectionTuples
			let isSelected = true;
			let categorySelections = _.pluck(selections, 'categories');
			_.each(categorySelections, resolvedTuple => {
				_.each(resolvedTuple, tupleValue => {
					isSelected = isSelected ? !!_.find(selectionTuples, selectedTuple => {
						return selectedTuple.columnId === tupleValue.columnId && selectedTuple.tupleItem.u === tupleValue.value;
					}) : false;
				});
			});
			return isSelected;
		}

		/**
		 * Get a selection object that corresponds to the supplied list of itemIds.
		 * @param itemIds	array of item ids to be considered part of the selection
		 *					If omitted, apply all dataitem
		 * @param {object} options  selection options to describe the selection
		 * @param {boolean} options.includeParents[includeParents=false]- if includes parents; default to false
		 * @param {boolean} options.ignoreEdgeSelections[ignoreEdgeSelections=false] - if includes parents; default to false
		 * @return selection object
		 */
		getSelection(itemIds, options) {
			if (!itemIds) {
				itemIds = ContentUtil.getColumnIdList(this._getVisualization().getSlots().getMappedSlotList(), 'attribute');
			}
			return this._getSelected(itemIds, options);
		}

		/**
		 * Return a proxy object that returns the selected items/tuples as an object for fast lookup.
		 * @param itemIds - the itemIds that form a tuple as an array ['Products', 'Years']
		 *					(also supports a single itemId)
		 * @returns a proxy object that returns an isSelected interface about what is selected by the itemIds.
		 *			or null if no selections exist for any of the set of items.
		 */
		_getSelected(itemIds, options) {
			if (!this._getVisualization().getDataSource()) {
				return null;
			}
			const visualization = this._getVisualization();
			const dataSource = visualization.getDataSource();
			const selected = new VisSelected({
				pageContextAPI: this._getPageContext(),
				itemIds: Array.isArray(itemIds) ? itemIds : [itemIds],
				scope: this._getScope(),
				eventGroupId: this._getEventGroupId(),
				sourceId: dataSource && dataSource.getId(),
				includeParents: (options && options.includeParents === true) ? true : false, //Default including parents to false (not applicable to charts)
				ignoreEdgeSelections: (options && options.ignoreEdgeSelections === true) ? true : false,
				synchronizeData: this._getController().synchronizeData // TODO will be replaced with synchronized feature soon
			});
			return (selected.hasSelections()) ? selected.getAPI() : null;
		}
	}

	return DataPointSelections;
});
